import {
  InputAdornment,
  debounce,
  IconButton,
  Typography,
} from "@mui/material";
import { ChangeEvent, ReactNode, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Block, Check, ContentCopy } from "@mui/icons-material";
import { useParams } from "react-router";
import { isCompanyPathAvailable } from "api/branding";
import copy from "copy-to-clipboard";
import { TOAST_TYPE, ToastContext } from "context/toastContext";
import AuturaTextField from "./AuturaTextField";
export interface CompanyPathInputProps {
  companyPath: string;
  setCompanyPath: (companyPath: string) => void;
  isAvailable: boolean;
  setIsAvailable: (isAvailable: boolean) => void;
  margin?: "dense" | "normal" | "none";
  variant?: "standard" | "outlined" | "filled";
  disabled?: boolean;
  fullWidth?: boolean;
  readonly?: boolean;
}

const CompanyPathInput = ({
  companyPath,
  setCompanyPath,
  isAvailable,
  setIsAvailable,
  margin,
  variant,
  disabled = false,
  fullWidth = true,
  readonly = false,
}: CompanyPathInputProps): ReactNode => {
  const displayToast = useContext(ToastContext);
  const { tenantID } = useParams();
  const { t } = useTranslation();

  useEffect(() => {
    debounce(async () => {
      checkCompanyPath(companyPath);
    }, 400)();
  }, [companyPath]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    // strip out any characters that are not url or local email safe
    const value = e.target.value.replace(/[^a-zA-Z0-9-]/g, "");
    setCompanyPath(value);
  };

  const checkCompanyPath = async (path: string) => {
    if (!path) return setIsAvailable(true);

    const res = await isCompanyPathAvailable({
      path: path,
      tenantID: tenantID as string,
    });
    
    if (res.ok) {
      const data = await res.json();
      setIsAvailable(data.available);
    }
  };

  const handleCopyToClipboard = async (): Promise<void> => {
    copy(`${window.CLIENT_BASE_URL}/${companyPath}`);
    displayToast({
      type: TOAST_TYPE.SUCCESS,
      message: t("toast.brandUrlCopied"),
    });
  };

  return (
    <AuturaTextField
      name={"tenant.data.companyPath"}
      label={t("tenant.data.companyPath", { ns: "entities" })}
      fullWidth={fullWidth}
      disabled={disabled}
      readonly={readonly}
      margin={margin}
      variant={variant}
      maxLength={64}
      helperText={t("helpers.company_path", { ns: "tenantSettings" })}
      startAdornment={`${window.CLIENT_BASE_URL}/`}
      endAdornment={
        isAvailable ? (
          <InputAdornment position="end">
            <Check color={"success"} />
            <IconButton
              size="small"
              color="success"
              onClick={() => handleCopyToClipboard()}
            >
              <ContentCopy />
            </IconButton>
          </InputAdornment>
        ) : (
          <InputAdornment position="end">
            <Typography variant="subtitle2" color="error">
              {"Already In Use"}
            </Typography>
            <Block color={"error"} />
          </InputAdornment>
        )
      }
      value={companyPath}
      onChange={handleChange}
      validators={[]}
    />
  );
};

export default CompanyPathInput;
